import { useState, useEffect } from 'react';
import { Activity, Users, FileText, DollarSign, Bell, LogOut, CheckCircle, XCircle, Eye, Send, Download, Languages, Camera, Edit, Save, X, Lock, Trash2, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { adminAPI, notificationAPI } from '../utils/api';
import BlogManager from '../components/BlogManager';
import CMSManager from '../components/CMSManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard({ user, onLogout }) {
  const [stats, setStats] = useState(null);
  const [pendingDoctors, setPendingDoctors] = useState([]);
  const [allDoctors, setAllDoctors] = useState([]);
  const [pendingCases, setPendingCases] = useState([]);
  const [pendingReports, setPendingReports] = useState([]);
  const [allCases, setAllCases] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [selectedLanguage, setSelectedLanguage] = useState({});
  const [processingReport, setProcessingReport] = useState(null);
  const [editingDoctor, setEditingDoctor] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [previewReport, setPreviewReport] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [passwordForm, setPasswordForm] = useState({ current: '', new: '', confirm: '' });
  const [changingPassword, setChangingPassword] = useState(false);
  const [resettingUsers, setResettingUsers] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [allPatients, setAllPatients] = useState([]);
  const [doctorsForDeletion, setDoctorsForDeletion] = useState([]);
  const [patientsForDeletion, setPatientsForDeletion] = useState([]);
  const [selectedDoctors, setSelectedDoctors] = useState([]);
  const [selectedPatients, setSelectedPatients] = useState([]);
  const [deletingUsers, setDeletingUsers] = useState(false);

  const languages = ['Russian', 'Turkish', 'English', 'German', 'French', 'Arabic', 'Chinese'];

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    await Promise.all([
      fetchStats(),
      fetchPendingDoctors(),
      fetchAllDoctors(),
      fetchPendingCases(),
      fetchPendingReports(),
      fetchAllCases(),
      fetchNotifications(),
      fetchAllPatients(),
      fetchUsersForDeletion()
    ]);
    setLoading(false);
  };

  const fetchStats = async () => {
    try {
      const response = await adminAPI.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load statistics');
    }
  };

  const fetchPendingDoctors = async () => {
    try {
      const response = await adminAPI.getPendingDoctors();
      setPendingDoctors(response.data);
    } catch (error) {
      console.error('Failed to load pending doctors');
    }
  };

  const fetchAllDoctors = async () => {
    try {
      const response = await adminAPI.getAllDoctors();
      setAllDoctors(response.data);
    } catch (error) {
      console.error('Failed to load all doctors');
    }
  };

  const fetchPendingCases = async () => {
    try {
      const response = await adminAPI.getCasesPendingReview();
      setPendingCases(response.data);
    } catch (error) {
      console.error('Failed to load pending cases');
    }
  };

  const fetchPendingReports = async () => {
    try {
      const response = await adminAPI.getPendingReports();
      setPendingReports(response.data);
    } catch (error) {
      console.error('Failed to load pending reports');
    }
  };

  const fetchAllCases = async () => {
    try {
      const response = await adminAPI.getAllCases();
      setAllCases(response.data);
    } catch (error) {
      console.error('Failed to load cases');
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await notificationAPI.getNotifications();
      setNotifications(response.data);
    } catch (error) {
      console.error('Failed to load notifications');
    }
  };

  const fetchAllPatients = async () => {
    try {
      const response = await adminAPI.getAllPatients();
      setAllPatients(response.data);
    } catch (error) {
      console.error('Failed to load patients');
    }
  };

  const fetchUsersForDeletion = async () => {
    try {
      const [doctorsRes, patientsRes] = await Promise.all([
        adminAPI.getDoctorsForDeletion(),
        adminAPI.getPatientsForDeletion()
      ]);
      setDoctorsForDeletion(doctorsRes.data);
      setPatientsForDeletion(patientsRes.data);
    } catch (error) {
      console.error('Failed to load users for deletion');
    }
  };

  // Doctor approval handlers
  const handleApproveDoctor = async (userId) => {
    try {
      await adminAPI.approveDoctor(userId);
      toast.success('Doktor onaylandı');
      fetchPendingDoctors();
      fetchAllDoctors();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Doktor onaylanamadı');
    }
  };

  const handleRejectDoctor = async (userId) => {
    try {
      await adminAPI.rejectDoctor(userId);
      toast.success('Doktor reddedildi');
      fetchPendingDoctors();
      fetchAllDoctors();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Doktor reddedilemedi');
    }
  };

  // Doctor profile management
  const handleEditDoctor = (doctor) => {
    setEditingDoctor(doctor.user.id);
    setEditForm({
      specialty: doctor.profile?.specialty || '',
      experience_years: doctor.profile?.experience_years || 0,
      license_number: doctor.profile?.license_number || '',
      price_per_case: doctor.profile?.price_per_case || 0,
      bio: doctor.profile?.bio || ''
    });
  };

  const handleSaveDoctor = async (userId) => {
    try {
      await adminAPI.updateDoctorProfile(userId, editForm);
      toast.success('Doktor bilgileri güncellendi');
      setEditingDoctor(null);
      fetchAllDoctors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Güncelleme başarısız');
    }
  };

  const handleUploadDoctorPhoto = async (userId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      await adminAPI.uploadDoctorPhoto(userId, formData);
      toast.success('Fotoğraf yüklendi');
      fetchAllDoctors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fotoğraf yüklenemedi');
    }
  };

  // Case approval handlers
  const handleApproveCaseForDoctor = async (caseId) => {
    try {
      await adminAPI.approveCaseForDoctor(caseId);
      toast.success('Vaka onaylandı, hastaya ödeme bildirimi gönderildi');
      fetchPendingCases();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Vaka onaylanamadı');
    }
  };

  const handleRejectCase = async (caseId) => {
    const reason = prompt('Red sebebini girin:');
    if (reason === null) return;
    try {
      await adminAPI.rejectCase(caseId, reason);
      toast.success('Vaka reddedildi');
      fetchPendingCases();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Vaka reddedilemedi');
    }
  };

  // Report approval and translation handler
  const handleApproveTranslateSend = async (reportId) => {
    const language = selectedLanguage[reportId];
    if (!language) {
      toast.error('Lütfen bir dil seçin');
      return;
    }

    setProcessingReport(reportId);
    try {
      await adminAPI.approveTranslateSend(reportId, language);
      toast.success(`Rapor onaylandı, ${language} diline çevrildi ve hastaya gönderildi!`);
      setPreviewReport(null);
      setPreviewData(null);
      fetchPendingReports();
      fetchStats();
      fetchAllCases();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İşlem başarısız');
    } finally {
      setProcessingReport(null);
    }
  };

  // Open preview modal
  const handleOpenPreview = (item) => {
    setPreviewData(item);
    setPreviewReport(item.report?.id);
  };

  // Close preview modal
  const handleClosePreview = () => {
    setPreviewReport(null);
    setPreviewData(null);
  };

  // Change admin password
  const handleChangePassword = async () => {
    if (!passwordForm.current || !passwordForm.new || !passwordForm.confirm) {
      toast.error('Tüm alanları doldurun');
      return;
    }
    if (passwordForm.new !== passwordForm.confirm) {
      toast.error('Yeni şifreler eşleşmiyor');
      return;
    }
    if (passwordForm.new.length < 6) {
      toast.error('Yeni şifre en az 6 karakter olmalı');
      return;
    }

    setChangingPassword(true);
    try {
      await adminAPI.changePassword(passwordForm.current, passwordForm.new);
      toast.success('Şifre başarıyla değiştirildi');
      setPasswordForm({ current: '', new: '', confirm: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Şifre değiştirilemedi');
    } finally {
      setChangingPassword(false);
    }
  };

  // Reset all users except admin
  const handleResetAllUsers = async () => {
    setResettingUsers(true);
    try {
      const response = await adminAPI.resetAllUsers();
      toast.success(`Tüm veriler silindi: ${response.data.deleted.users} kullanıcı, ${response.data.deleted.cases} vaka`);
      setShowResetConfirm(false);
      fetchStats();
      fetchAllCases();
      fetchPendingDoctors();
      fetchAllDoctors();
      fetchPendingReports();
      fetchPendingCases();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sıfırlama işlemi başarısız');
    } finally {
      setResettingUsers(false);
    }
  };

  // Delete selected users
  const handleDeleteSelectedUsers = async (type) => {
    const userIds = type === 'doctors' ? selectedDoctors : selectedPatients;
    if (userIds.length === 0) {
      toast.error('Lütfen silmek istediğiniz kullanıcıları seçin');
      return;
    }

    setDeletingUsers(true);
    try {
      const response = await adminAPI.deleteSelectedUsers(userIds);
      toast.success(response.data.message);
      if (type === 'doctors') {
        setSelectedDoctors([]);
      } else {
        setSelectedPatients([]);
      }
      fetchUsersForDeletion();
      fetchAllPatients();
      fetchStats();
      fetchAllCases();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Silme işlemi başarısız');
    } finally {
      setDeletingUsers(false);
    }
  };

  // Toggle user selection
  const toggleDoctorSelection = (userId) => {
    setSelectedDoctors(prev => 
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const togglePatientSelection = (userId) => {
    setSelectedPatients(prev => 
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  // Select all
  const selectAllDoctors = () => {
    if (selectedDoctors.length === doctorsForDeletion.length) {
      setSelectedDoctors([]);
    } else {
      setSelectedDoctors(doctorsForDeletion.map(d => d.id));
    }
  };

  const selectAllPatients = () => {
    if (selectedPatients.length === patientsForDeletion.length) {
      setSelectedPatients([]);
    } else {
      setSelectedPatients(patientsForDeletion.map(p => p.id));
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'completed': 'bg-emerald-50 text-emerald-700',
      'in_review': 'bg-blue-50 text-blue-700',
      'waiting': 'bg-amber-50 text-amber-700',
      'pending_payment': 'bg-orange-50 text-orange-700',
      'pending_admin_review': 'bg-purple-50 text-purple-700',
      'report_submitted': 'bg-cyan-50 text-cyan-700',
      'approved': 'bg-emerald-50 text-emerald-700',
      'pending': 'bg-amber-50 text-amber-700',
      'rejected': 'bg-red-50 text-red-700'
    };
    return colors[status] || 'bg-slate-50 text-slate-700';
  };

  const getStatusText = (status) => {
    const texts = {
      'pending_admin_review': 'Admin Onayı Bekliyor',
      'pending_payment': 'Ödeme Bekliyor',
      'waiting': 'Doktor Bekliyor',
      'in_review': 'İnceleniyor',
      'report_submitted': 'Rapor Onayı Bekliyor',
      'completed': 'Tamamlandı',
      'declined': 'Reddedildi',
      'refunded': 'İade Edildi',
      'approved': 'Onaylı',
      'pending': 'Beklemede',
      'rejected': 'Reddedildi'
    };
    return texts[status] || status;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Activity className="w-8 h-8 text-primary" />
            <span className="text-xl font-bold text-slate-900">Admin Panel</span>
          </div>
          <div className="flex items-center gap-4">
            <button className="relative p-2 hover:bg-slate-100 rounded-lg">
              <Bell className="w-5 h-5 text-slate-600" />
              {notifications.filter(n => !n.read).length > 0 && (
                <span className="absolute top-1 right-1 w-2 h-2 bg-accent rounded-full"></span>
              )}
            </button>
            <Button onClick={onLogout} variant="ghost" className="text-slate-600">
              <LogOut className="w-4 h-4 mr-2" />Çıkış
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 border-b border-slate-200 overflow-x-auto pb-2">
          {[
            { id: 'overview', label: 'Genel Bakış' },
            { id: 'cases-review', label: 'Görüntü Onayı', badge: pendingCases.length },
            { id: 'reports-review', label: 'Rapor Onayı', badge: pendingReports.length },
            { id: 'doctors-pending', label: 'Doktor Onayı', badge: pendingDoctors.length },
            { id: 'doctors-manage', label: 'Doktor Yönetimi' },
            { id: 'patients', label: 'Hastalar', badge: allPatients.length },
            { id: 'cases', label: 'Tüm Vakalar' },
            { id: 'blog', label: 'Blog' },
            { id: 'cms', label: 'Site Ayarları' },
            { id: 'settings', label: 'Admin Ayarları' },
          ].map((tab) => (
            <button
              key={tab.id}
              data-testid={`tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 font-medium transition-colors whitespace-nowrap rounded-t-lg ${
                activeTab === tab.id
                  ? 'text-primary bg-primary/10 border-b-2 border-primary'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
              }`}
            >
              {tab.label}
              {tab.badge > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-accent text-white text-xs rounded-full">{tab.badge}</span>
              )}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div>
            {loading ? (
              <div className="text-center py-12 text-slate-600">Yükleniyor...</div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                  <Card className="p-5 bg-white"><div className="flex items-center justify-between"><div><div className="text-xs text-slate-500 uppercase mb-1">Görüntü Onayı</div><div className="text-2xl font-bold text-purple-600">{stats?.pending_review || 0}</div></div><Eye className="w-8 h-8 text-purple-200" /></div></Card>
                  <Card className="p-5 bg-white"><div className="flex items-center justify-between"><div><div className="text-xs text-slate-500 uppercase mb-1">Rapor Onayı</div><div className="text-2xl font-bold text-cyan-600">{stats?.pending_reports || 0}</div></div><FileText className="w-8 h-8 text-cyan-200" /></div></Card>
                  <Card className="p-5 bg-white"><div className="flex items-center justify-between"><div><div className="text-xs text-slate-500 uppercase mb-1">Escrow'da</div><div className="text-2xl font-bold text-amber-600">${stats?.escrow_held?.toFixed(0) || 0}</div></div><DollarSign className="w-8 h-8 text-amber-200" /></div></Card>
                  <Card className="p-5 bg-white"><div className="flex items-center justify-between"><div><div className="text-xs text-slate-500 uppercase mb-1">Toplam Gelir</div><div className="text-2xl font-bold text-emerald-600">${stats?.total_revenue?.toFixed(0) || 0}</div></div><DollarSign className="w-8 h-8 text-emerald-200" /></div></Card>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card className="p-5 bg-white"><h3 className="font-semibold mb-3">Vaka Durumu</h3><div className="space-y-2 text-sm"><div className="flex justify-between"><span className="text-slate-500">Toplam</span><span className="font-medium">{stats?.total_cases || 0}</span></div><div className="flex justify-between"><span className="text-slate-500">Aktif</span><span className="font-medium">{stats?.pending_cases || 0}</span></div><div className="flex justify-between"><span className="text-slate-500">Tamamlanan</span><span className="font-medium">{stats?.completed_cases || 0}</span></div></div></Card>
                  <Card className="p-5 bg-white"><h3 className="font-semibold mb-3">Doktorlar</h3><div className="space-y-2 text-sm"><div className="flex justify-between"><span className="text-slate-500">Toplam</span><span className="font-medium">{stats?.total_doctors || 0}</span></div><div className="flex justify-between"><span className="text-slate-500">Onay Bekleyen</span><span className="font-medium">{stats?.pending_doctors || 0}</span></div></div></Card>
                  <Card className="p-5 bg-white"><h3 className="font-semibold mb-3">Ödemeler</h3><div className="space-y-2 text-sm"><div className="flex justify-between"><span className="text-slate-500">Escrow'da</span><span className="font-medium">${stats?.escrow_held?.toFixed(2) || 0}</span></div><div className="flex justify-between"><span className="text-slate-500">Serbest Bırakılan</span><span className="font-medium">${stats?.escrow_released?.toFixed(2) || 0}</span></div></div></Card>
                </div>
              </>
            )}
          </div>
        )}

        {/* Cases Review Tab */}
        {activeTab === 'cases-review' && (
          <div>
            <h2 className="text-xl font-bold mb-4">Görüntü Onayı Bekleyen Vakalar</h2>
            {pendingCases.length === 0 ? (
              <Card className="p-12 bg-white text-center"><Eye className="w-12 h-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">Onay bekleyen görüntü yok</p></Card>
            ) : (
              <div className="space-y-4">
                {pendingCases.map((item) => (
                  <Card key={item.case.id} className="p-5 bg-white">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="font-semibold text-lg">{item.case.exam_type}</h3>
                        <p className="text-sm text-slate-500">Hasta: {item.patient?.first_name} {item.patient?.last_name} • {item.patient?.phone}</p>
                        <p className="text-sm text-slate-500">Doktor: Dr. {item.doctor?.first_name} {item.doctor?.last_name}</p>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-semibold text-primary">${item.case.price}</div>
                        {item.case.is_urgent && <Badge className="bg-red-100 text-red-700">ACİL</Badge>}
                      </div>
                    </div>
                    {item.files?.length > 0 && (
                      <div className="mb-4">
                        <p className="text-sm font-medium mb-2">Dosyalar ({item.files.length}):</p>
                        <div className="flex flex-wrap gap-2">
                          {item.files.map((file) => (
                            <a key={file.file_id} href={`${BACKEND_URL}/api/files/${file.file_id}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 px-3 py-1.5 bg-slate-100 rounded text-sm hover:bg-slate-200">
                              <Download className="w-3 h-3" />{file.filename}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="flex gap-3">
                      <Button onClick={() => handleApproveCaseForDoctor(item.case.id)} className="bg-emerald-600 text-white hover:bg-emerald-700"><CheckCircle className="w-4 h-4 mr-2" />Onayla</Button>
                      <Button onClick={() => handleRejectCase(item.case.id)} variant="outline" className="border-red-300 text-red-600"><XCircle className="w-4 h-4 mr-2" />Reddet</Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Reports Review Tab */}
        {activeTab === 'reports-review' && (
          <div>
            <h2 className="text-xl font-bold mb-4">Rapor Onayı ve Çeviri</h2>
            {pendingReports.length === 0 ? (
              <Card className="p-12 bg-white text-center"><FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">Onay bekleyen rapor yok</p></Card>
            ) : (
              <div className="space-y-4">
                {pendingReports.map((item) => (
                  <Card key={item.report?.id} className="p-5 bg-white">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="font-semibold text-lg">{item.case?.exam_type}</h3>
                        <p className="text-sm text-slate-500">Hasta: {item.patient?.first_name} {item.patient?.last_name} ({item.patient?.email})</p>
                        <p className="text-sm text-slate-500">Doktor: Dr. {item.doctor?.first_name} {item.doctor?.last_name}</p>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-semibold text-primary">${item.payment?.amount || item.case?.price}</div>
                        <div className="text-xs text-slate-500">Doktor: ${item.payment?.doctor_earnings?.toFixed(2)}</div>
                      </div>
                    </div>
                    
                    {/* Preview Button */}
                    <div className="mb-4">
                      <Button 
                        onClick={() => handleOpenPreview(item)} 
                        variant="outline" 
                        className="w-full border-primary text-primary hover:bg-primary/5"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Rapor Ön İzleme
                      </Button>
                    </div>
                    
                    <div className="flex items-center gap-3 p-3 bg-cyan-50 rounded-lg">
                      <Languages className="w-5 h-5 text-cyan-600" />
                      <select value={selectedLanguage[item.report?.id] || ''} onChange={(e) => setSelectedLanguage({...selectedLanguage, [item.report?.id]: e.target.value})} className="flex-1 px-3 py-2 border rounded-lg text-sm">
                        <option value="">Çeviri dili seçin...</option>
                        {languages.map(lang => <option key={lang} value={lang}>{lang}</option>)}
                      </select>
                      <Button onClick={() => handleApproveTranslateSend(item.report?.id)} disabled={!selectedLanguage[item.report?.id] || processingReport === item.report?.id} className="bg-cyan-600 text-white hover:bg-cyan-700 disabled:opacity-50">
                        {processingReport === item.report?.id ? 'İşleniyor...' : <><Send className="w-4 h-4 mr-2" />Onayla ve Gönder</>}
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Doctor Pending Approvals */}
        {activeTab === 'doctors-pending' && (
          <div>
            <h2 className="text-xl font-bold mb-4">Doktor Onayları</h2>
            {pendingDoctors.length === 0 ? (
              <Card className="p-12 bg-white text-center"><Users className="w-12 h-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">Onay bekleyen doktor yok</p></Card>
            ) : (
              <div className="space-y-4">
                {pendingDoctors.map((item) => (
                  <Card key={item.user.id} className="p-5 bg-white">
                    <div className="flex gap-4">
                      <div className="w-20 h-20 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden">
                        {item.profile?.photo_url ? (
                          <img src={`${BACKEND_URL}${item.profile.photo_url}`} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <Users className="w-8 h-8 text-slate-400" />
                        )}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg">Dr. {item.user.first_name} {item.user.last_name}</h3>
                        <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                          <div><span className="text-slate-500">Uzmanlık:</span> {item.profile?.specialty}</div>
                          <div><span className="text-slate-500">Deneyim:</span> {item.profile?.experience_years} yıl</div>
                          <div><span className="text-slate-500">Lisans:</span> {item.profile?.license_number}</div>
                          <div><span className="text-slate-500">Ücret:</span> ${item.profile?.price_per_case}</div>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-3 mt-4">
                      <Button onClick={() => handleApproveDoctor(item.user.id)} className="bg-emerald-600 text-white"><CheckCircle className="w-4 h-4 mr-2" />Onayla</Button>
                      <Button onClick={() => handleRejectDoctor(item.user.id)} variant="outline" className="border-red-300 text-red-600"><XCircle className="w-4 h-4 mr-2" />Reddet</Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Doctor Management Tab */}
        {activeTab === 'doctors-manage' && (
          <div>
            <h2 className="text-xl font-bold mb-4">Doktor Yönetimi</h2>
            {allDoctors.length === 0 ? (
              <Card className="p-12 bg-white text-center"><Users className="w-12 h-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">Henüz doktor yok</p></Card>
            ) : (
              <div className="space-y-4">
                {allDoctors.map((item) => (
                  <Card key={item.user.id} className="p-5 bg-white">
                    <div className="flex gap-4">
                      {/* Photo Section */}
                      <div className="relative">
                        <div className="w-24 h-24 rounded-xl bg-slate-200 flex items-center justify-center overflow-hidden">
                          {item.profile?.photo_url ? (
                            <img src={`${BACKEND_URL}${item.profile.photo_url}`} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <Users className="w-10 h-10 text-slate-400" />
                          )}
                        </div>
                        <label className="absolute -bottom-2 -right-2 w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center cursor-pointer hover:bg-primary/90">
                          <Camera className="w-4 h-4" />
                          <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && handleUploadDoctorPhoto(item.user.id, e.target.files[0])} />
                        </label>
                      </div>

                      {/* Info Section */}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold text-lg">Dr. {item.user.first_name} {item.user.last_name}</h3>
                          <Badge className={getStatusColor(item.profile?.status)}>{getStatusText(item.profile?.status)}</Badge>
                        </div>
                        <p className="text-sm text-slate-500 mb-3">{item.user.email} • {item.user.phone}</p>

                        {editingDoctor === item.user.id ? (
                          /* Edit Form */
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-xs text-slate-500">Uzmanlık</label>
                              <input type="text" value={editForm.specialty} onChange={(e) => setEditForm({...editForm, specialty: e.target.value})} className="w-full px-3 py-2 border rounded text-sm" />
                            </div>
                            <div>
                              <label className="text-xs text-slate-500">Deneyim (yıl)</label>
                              <input type="number" value={editForm.experience_years} onChange={(e) => setEditForm({...editForm, experience_years: parseInt(e.target.value)})} className="w-full px-3 py-2 border rounded text-sm" />
                            </div>
                            <div>
                              <label className="text-xs text-slate-500">Lisans No</label>
                              <input type="text" value={editForm.license_number} onChange={(e) => setEditForm({...editForm, license_number: e.target.value})} className="w-full px-3 py-2 border rounded text-sm" />
                            </div>
                            <div>
                              <label className="text-xs text-slate-500">Ücret ($)</label>
                              <input type="number" value={editForm.price_per_case} onChange={(e) => setEditForm({...editForm, price_per_case: parseFloat(e.target.value)})} className="w-full px-3 py-2 border rounded text-sm" />
                            </div>
                            <div className="col-span-2">
                              <label className="text-xs text-slate-500">Biyografi</label>
                              <textarea value={editForm.bio} onChange={(e) => setEditForm({...editForm, bio: e.target.value})} className="w-full px-3 py-2 border rounded text-sm" rows={2} />
                            </div>
                            <div className="col-span-2 flex gap-2">
                              <Button onClick={() => handleSaveDoctor(item.user.id)} className="bg-emerald-600 text-white"><Save className="w-4 h-4 mr-2" />Kaydet</Button>
                              <Button onClick={() => setEditingDoctor(null)} variant="outline"><X className="w-4 h-4 mr-2" />İptal</Button>
                            </div>
                          </div>
                        ) : (
                          /* View Mode */
                          <>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                              <div><span className="text-slate-500">Uzmanlık:</span><br/><span className="font-medium">{item.profile?.specialty || '-'}</span></div>
                              <div><span className="text-slate-500">Deneyim:</span><br/><span className="font-medium">{item.profile?.experience_years || 0} yıl</span></div>
                              <div><span className="text-slate-500">Lisans:</span><br/><span className="font-medium">{item.profile?.license_number || '-'}</span></div>
                              <div><span className="text-slate-500">Ücret:</span><br/><span className="font-medium text-primary">${item.profile?.price_per_case || 0}</span></div>
                            </div>
                            {item.profile?.bio && <p className="text-sm text-slate-600 mt-2 italic">"{item.profile.bio}"</p>}
                            <Button onClick={() => handleEditDoctor(item)} variant="outline" className="mt-3"><Edit className="w-4 h-4 mr-2" />Düzenle</Button>
                          </>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* All Cases Tab */}
        {activeTab === 'cases' && (
          <div>
            <h2 className="text-xl font-bold mb-4">Tüm Vakalar</h2>
            {allCases.length === 0 ? (
              <Card className="p-12 bg-white text-center"><FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">Henüz vaka yok</p></Card>
            ) : (
              <div className="space-y-3">
                {allCases.map((c) => (
                  <Card key={c.id} className="p-4 bg-white">
                    <div className="flex justify-between items-center">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{c.exam_type}</span>
                          <Badge className={`${getStatusColor(c.status)} text-xs`}>{getStatusText(c.status)}</Badge>
                          {c.is_urgent && <Badge className="bg-red-100 text-red-700 text-xs">ACIL</Badge>}
                        </div>
                        <p className="text-sm text-slate-500 mt-1">{c.patient_name} → Dr. {c.doctor_name}</p>
                        <p className="text-xs text-slate-400">ID: {c.id}</p>
                      </div>
                      <div className="flex items-center gap-4">
                        {c.status === 'completed' && c.pdf_file_id && (
                          <a
                            href={`${BACKEND_URL}/api/files/${c.pdf_file_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 px-3 py-2 bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 transition-colors text-sm font-medium"
                          >
                            <Download className="w-4 h-4" />
                            PDF Rapor
                          </a>
                        )}
                        <div className="text-right">
                          <div className="font-semibold text-primary">${c.price}</div>
                          <div className="text-xs text-slate-400">{new Date(c.created_at).toLocaleDateString('tr-TR')}</div>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
        
        {activeTab === 'blog' && <BlogManager />}
        {activeTab === 'cms' && <CMSManager />}
        
        {/* Patients Tab */}
        {activeTab === 'patients' && (
          <div>
            <h2 className="text-xl font-bold mb-4">Kayıtlı Hastalar ({allPatients.length})</h2>
            {allPatients.length === 0 ? (
              <Card className="p-12 bg-white text-center"><Users className="w-12 h-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">Henüz hasta yok</p></Card>
            ) : (
              <Card className="bg-white overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Ad Soyad</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Telefon</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">E-posta</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Kayıt Tarihi</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {allPatients.map((patient) => (
                      <tr key={patient.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <span className="font-medium text-slate-900">{patient.first_name} {patient.last_name}</span>
                        </td>
                        <td className="px-4 py-3 text-slate-600">{patient.phone}</td>
                        <td className="px-4 py-3 text-slate-600">{patient.email}</td>
                        <td className="px-4 py-3 text-slate-500 text-sm">
                          {patient.created_at ? new Date(patient.created_at).toLocaleDateString('tr-TR') : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            )}
          </div>
        )}
        
        {/* Admin Settings Tab */}
        {activeTab === 'settings' && (
          <div className="space-y-6">
            <h2 className="text-xl font-bold">Admin Ayarları</h2>
            
            {/* Change Password */}
            <Card className="p-6 bg-white">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Lock className="w-5 h-5 text-primary" />
                </div>
                <h3 className="text-lg font-semibold">Şifre Değiştir</h3>
              </div>
              <div className="space-y-4 max-w-md">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Mevcut Şifre</label>
                  <input
                    type="password"
                    value={passwordForm.current}
                    onChange={(e) => setPasswordForm({...passwordForm, current: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Mevcut şifrenizi girin"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Yeni Şifre</label>
                  <input
                    type="password"
                    value={passwordForm.new}
                    onChange={(e) => setPasswordForm({...passwordForm, new: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Yeni şifrenizi girin"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Yeni Şifre (Tekrar)</label>
                  <input
                    type="password"
                    value={passwordForm.confirm}
                    onChange={(e) => setPasswordForm({...passwordForm, confirm: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Yeni şifrenizi tekrar girin"
                  />
                </div>
                <Button
                  onClick={handleChangePassword}
                  disabled={changingPassword}
                  className="bg-primary text-white hover:bg-primary/90"
                >
                  {changingPassword ? 'Değiştiriliyor...' : 'Şifreyi Değiştir'}
                </Button>
              </div>
            </Card>
            
            {/* Reset All Users */}
            <Card className="p-6 bg-white border-red-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-red-100 rounded-lg">
                  <Trash2 className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-red-700">Kullanıcı Silme</h3>
              </div>
              <p className="text-sm text-slate-600 mb-4">
                Seçili kullanıcıları ve ilgili verilerini (vakalar, raporlar) siler. Bu işlem geri alınamaz!
              </p>
              
              {/* Doctors List */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-slate-700">Doktorlar ({doctorsForDeletion.length})</h4>
                  {doctorsForDeletion.length > 0 && (
                    <div className="flex items-center gap-2">
                      <button onClick={selectAllDoctors} className="text-sm text-primary hover:underline">
                        {selectedDoctors.length === doctorsForDeletion.length ? 'Seçimi Kaldır' : 'Tümünü Seç'}
                      </button>
                      {selectedDoctors.length > 0 && (
                        <Button
                          onClick={() => handleDeleteSelectedUsers('doctors')}
                          disabled={deletingUsers}
                          size="sm"
                          className="bg-red-600 text-white hover:bg-red-700"
                        >
                          {deletingUsers ? 'Siliniyor...' : `${selectedDoctors.length} Doktoru Sil`}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
                {doctorsForDeletion.length === 0 ? (
                  <p className="text-sm text-slate-500 italic">Silinecek doktor yok</p>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto border border-slate-200 rounded-lg p-2">
                    {doctorsForDeletion.map((doc) => (
                      <label key={doc.id} className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedDoctors.includes(doc.id)}
                          onChange={() => toggleDoctorSelection(doc.id)}
                          className="w-4 h-4 text-red-600 rounded border-slate-300"
                        />
                        <div className="flex-1">
                          <span className="font-medium">Dr. {doc.first_name} {doc.last_name}</span>
                          <span className="text-sm text-slate-500 ml-2">({doc.email})</span>
                        </div>
                        <span className="text-xs text-slate-400">{doc.case_count} vaka</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Patients List */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-slate-700">Hastalar ({patientsForDeletion.length})</h4>
                  {patientsForDeletion.length > 0 && (
                    <div className="flex items-center gap-2">
                      <button onClick={selectAllPatients} className="text-sm text-primary hover:underline">
                        {selectedPatients.length === patientsForDeletion.length ? 'Seçimi Kaldır' : 'Tümünü Seç'}
                      </button>
                      {selectedPatients.length > 0 && (
                        <Button
                          onClick={() => handleDeleteSelectedUsers('patients')}
                          disabled={deletingUsers}
                          size="sm"
                          className="bg-red-600 text-white hover:bg-red-700"
                        >
                          {deletingUsers ? 'Siliniyor...' : `${selectedPatients.length} Hastayı Sil`}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
                {patientsForDeletion.length === 0 ? (
                  <p className="text-sm text-slate-500 italic">Silinecek hasta yok</p>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto border border-slate-200 rounded-lg p-2">
                    {patientsForDeletion.map((pat) => (
                      <label key={pat.id} className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedPatients.includes(pat.id)}
                          onChange={() => togglePatientSelection(pat.id)}
                          className="w-4 h-4 text-red-600 rounded border-slate-300"
                        />
                        <div className="flex-1">
                          <span className="font-medium">{pat.first_name} {pat.last_name}</span>
                          <span className="text-sm text-slate-500 ml-2">({pat.email})</span>
                        </div>
                        <span className="text-xs text-slate-400">{pat.case_count} vaka</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </Card>
          </div>
        )}
      </div>

      {/* Report Preview Modal */}
      {previewReport && previewData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl">
            {/* Modal Header */}
            <div className="p-4 border-b border-slate-200 flex justify-between items-center">
              <div>
                <h3 className="text-xl font-bold text-slate-900">Rapor Ön İzleme</h3>
                <p className="text-sm text-slate-500">Onaylamadan önce raporu kontrol edin</p>
              </div>
              <button
                onClick={handleClosePreview}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            
            {/* Report Preview Content */}
            <div className="flex-1 overflow-auto p-6 bg-slate-50">
              {/* Header */}
              <div className="bg-white rounded-lg shadow-sm p-6 mb-4 border border-slate-200">
                <div className="text-center mb-4">
                  <h2 className="text-2xl font-bold text-teal-600">www.vtoroemnenie.com</h2>
                  <p className="text-sm text-slate-500">Medical Second Opinion Platform</p>
                  <div className="mt-2 py-1 px-3 bg-red-100 text-red-600 text-sm font-bold rounded inline-block">
                    *** ÖN İZLEME / PREVIEW ***
                  </div>
                </div>
                
                <h3 className="text-lg font-bold text-center text-slate-800 mb-4">
                  MEDICAL SECOND OPINION REPORT
                </h3>
                
                {/* Patient & Doctor Info */}
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="p-3 bg-purple-50 rounded-lg">
                    <h4 className="text-xs font-bold text-purple-600 uppercase mb-2">Hasta Bilgileri</h4>
                    <p className="text-sm"><span className="text-slate-500">Ad Soyad:</span> {previewData.patient?.first_name} {previewData.patient?.last_name}</p>
                    <p className="text-sm"><span className="text-slate-500">Email:</span> {previewData.patient?.email}</p>
                    <p className="text-sm"><span className="text-slate-500">Telefon:</span> {previewData.patient?.phone}</p>
                  </div>
                  <div className="p-3 bg-teal-50 rounded-lg">
                    <h4 className="text-xs font-bold text-teal-600 uppercase mb-2">Doktor Bilgileri</h4>
                    <p className="text-sm"><span className="text-slate-500">Ad Soyad:</span> Dr. {previewData.doctor?.first_name} {previewData.doctor?.last_name}</p>
                    <p className="text-sm"><span className="text-slate-500">Uzmanlık:</span> {previewData.doctor_profile?.specialty || '-'}</p>
                    <p className="text-sm"><span className="text-slate-500">Lisans No:</span> {previewData.doctor_profile?.license_number || '-'}</p>
                  </div>
                </div>
                
                {/* Case Info */}
                <div className="p-3 bg-slate-50 rounded-lg mt-4">
                  <h4 className="text-xs font-bold text-slate-600 uppercase mb-2">Vaka Bilgileri</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <p><span className="text-slate-500">Vaka ID:</span> {previewData.case?.id}</p>
                    <p><span className="text-slate-500">Tetkik Tipi:</span> {previewData.case?.exam_type}</p>
                    <p><span className="text-slate-500">Tarih:</span> {new Date(previewData.report?.created_at).toLocaleString('tr-TR')}</p>
                    <p><span className="text-slate-500">Ücret:</span> ${previewData.case?.price}</p>
                  </div>
                </div>
              </div>
              
              {/* Report Sections */}
              <div className="bg-white rounded-lg shadow-sm p-6 border border-slate-200 space-y-6">
                <div>
                  <h4 className="text-sm font-bold text-teal-600 uppercase mb-2">Findings (Bulgular)</h4>
                  <p className="text-slate-700 whitespace-pre-wrap">{previewData.report?.findings}</p>
                </div>
                
                <div>
                  <h4 className="text-sm font-bold text-teal-600 uppercase mb-2">Impression (İzlenim)</h4>
                  <p className="text-slate-700 whitespace-pre-wrap">{previewData.report?.impression}</p>
                </div>
                
                <div>
                  <h4 className="text-sm font-bold text-teal-600 uppercase mb-2">Recommendations (Öneriler)</h4>
                  <p className="text-slate-700 whitespace-pre-wrap">{previewData.report?.recommendations}</p>
                </div>
              </div>
              
              {/* Info Note */}
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs text-amber-800">
                  <strong>Not:</strong> Onayladığınızda rapor seçilen dile çevrilecek, PDF olarak kaydedilecek ve hastaya gönderilecektir.
                </p>
              </div>
            </div>
            
            {/* Modal Footer */}
            <div className="p-4 border-t border-slate-200">
              <div className="flex items-center gap-3 p-3 bg-cyan-50 rounded-lg">
                <Languages className="w-5 h-5 text-cyan-600" />
                <select 
                  value={selectedLanguage[previewData.report?.id] || ''} 
                  onChange={(e) => setSelectedLanguage({...selectedLanguage, [previewData.report?.id]: e.target.value})} 
                  className="flex-1 px-3 py-2 border rounded-lg text-sm"
                >
                  <option value="">Çeviri dili seçin...</option>
                  {languages.map(lang => <option key={lang} value={lang}>{lang}</option>)}
                </select>
                <Button 
                  variant="outline" 
                  onClick={handleClosePreview}
                  className="px-6"
                >
                  Kapat
                </Button>
                <Button 
                  onClick={() => handleApproveTranslateSend(previewData.report?.id)} 
                  disabled={!selectedLanguage[previewData.report?.id] || processingReport === previewData.report?.id} 
                  className="bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 px-6"
                >
                  {processingReport === previewData.report?.id ? 'İşleniyor...' : <><CheckCircle className="w-4 h-4 mr-2" />Onayla ve Gönder</>}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
